import { FC } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { StyledLogo, StyledPageHeader } from './PageHeader.styles';
import logoLightMode from '../../assets/img/logo-horizontal.png';
import logoDarkMode from '../../assets/img/logo-horizontal-inverted.png';
import placeholder from '../../assets/img/user-placeholder.png';
import { Avatar } from '../../atoms/Avatar';
import { PopupList } from '../../atoms/PopupList';
import { Button, ButtonSize, ButtonStyle } from '../../atoms/Button';
import { Link } from 'react-router-dom';
import useMediaQuery from '../../utils/useMediaQuery/useMediaQuery';

export interface UserWidgetProps {
  /** the function to call when the user clicks the login button */
  login: () => void;
  /** the function to call when the user clicks the logout button */
  logout: () => void;
  /** the url of the user's photo */
  userPhoto?: string;
  /** the user's name */
  userName?: string;
  /** whether or not the title is currently loading */
  loading?: boolean;
  /** whether the header should fill the width of the page */
  $fullWidth?: boolean;
}

/**
 * Primary page header component.
 */
export const PageHeader: FC<UserWidgetProps> = ({
  userPhoto,
  login,
  logout,
  userName,
  $fullWidth,
}) => {
  const isDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  return (
    <StyledPageHeader $fullWidth={$fullWidth}>
      <Link to="/" style={{ marginRight: 'auto' }}>
        <StyledLogo
          alt="UoS Logo"
          data-testid="logo"
          src={isDarkMode ? logoDarkMode : logoLightMode}
        />
      </Link>
      <AuthenticatedTemplate>
        <PopupList
          popupDescription="options relating to your account"
          items={
            <Button
              onClick={logout}
              $buttonSize={ButtonSize.Small}
              data-testid="logout"
            >
              Log Out
            </Button>
          }
        >
          <Avatar
            data-testid="user-avatar"
            image={userPhoto || placeholder}
            text={userName || 'Logged In'}
          />
        </PopupList>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Button
          $buttonSize={ButtonSize.Small}
          data-testid="login"
          $buttonStyle={ButtonStyle.Secondary}
          onClick={login}
        >
          Log In
        </Button>
      </UnauthenticatedTemplate>
    </StyledPageHeader>
  );
};

export default PageHeader;
